import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import {
  Menu,
  Popover,
  ActionList,
  ActionHeading,
  ActionItemButton,
  CheckSingle,
  Avatar,
  PersonSolid,
} from "@conserveit/blocks";
import { Trans, t } from "@lingui/macro";
import { I18n } from "@lingui/react";
import { setLocale } from "../../../redux/modules/_BETA/selections";
import { locales } from "../../../i18n";
import { $MenuWrapper } from "./UserMenu.styles";
import { logout } from "../../../redux/modules/auth/actionCreators";

function mapStateToProps(state) {
  return {
    locale: state.betaSelections.locale,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    selectLocale: (localeKey, cb) => () => {
      dispatch(setLocale(localeKey));
      cb();
    },
    onLogout: () => dispatch(logout()),
  };
}

const StyledList = styled(ActionList)`
  a {
    text-decoration: none;
  }
`;

const UserMenu = ({
  clientRoutes,
  loggedIn,
  selectLocale,
  locale,
  onLogout,
}) => {
  const { adminLinks, userDetails } = clientRoutes;
  return (
    <I18n>
      {({ i18n }) => (
        <$MenuWrapper>
          {!loggedIn && (
            <Link to="/login" href="/login">
              {i18n._(t("user_menu.login")`Login`)}
            </Link>
          )}
          <Menu>
            {({ isOpen, toggle, close }) => (
              <Fragment>
                {userDetails.name ? (
                  <Avatar
                    data-cy="UserMenu"
                    data-testid="UserMenu"
                    name={userDetails.name}
                    onClick={toggle}
                  />
                ) : (
                  <Avatar
                    data-cy="UserMenu"
                    data-testid="UserMenu"
                    icon={<PersonSolid />}
                    onClick={toggle}
                  />
                )}

                {isOpen && (
                  <Popover right>
                    {userDetails.name && (
                      <ActionHeading>
                        <Trans id="user_menu.salutation">
                          Hi, {userDetails.name}
                        </Trans>
                      </ActionHeading>
                    )}
                    <StyledList>
                      {adminLinks.profile && (
                        <ActionItemButton
                          key={adminLinks.profile.href}
                          as={Link}
                          onClick={close}
                          to={adminLinks.profile.href}
                          href={adminLinks.profile.href}
                        >
                          {adminLinks.profile.label(i18n)}
                        </ActionItemButton>
                      )}
                      {adminLinks.userAdmin && (
                        <ActionItemButton
                          key={adminLinks.userAdmin.href}
                          as={Link}
                          onClick={close}
                          to={adminLinks.userAdmin.href}
                          href={adminLinks.userAdmin.href}
                        >
                          {adminLinks.userAdmin.label(i18n)}
                        </ActionItemButton>
                      )}
                      {adminLinks.groupAdmin && (
                        <ActionItemButton
                          key={adminLinks.groupAdmin.href}
                          as={Link}
                          onClick={close}
                          to={adminLinks.groupAdmin.href}
                          href={adminLinks.groupAdmin.href}
                        >
                          {adminLinks.groupAdmin.label(i18n)}
                        </ActionItemButton>
                      )}
                      {adminLinks.siteSettings && (
                        <ActionItemButton
                          key={adminLinks.siteSettings.href}
                          as={Link}
                          onClick={close}
                          to={adminLinks.siteSettings.href}
                          href={adminLinks.siteSettings.href}
                        >
                          {adminLinks.siteSettings.label(i18n)}
                        </ActionItemButton>
                      )}
                      <ActionItemButton
                        as="a"
                        onClick={close}
                        href={adminLinks.support.href}
                      >
                        {adminLinks.support.label(i18n)}
                      </ActionItemButton>
                      {!loggedIn ? (
                        <ActionItemButton
                          as={Link}
                          onClick={close}
                          to={adminLinks.auth.href}
                          href={adminLinks.auth.href}
                        >
                          {adminLinks.auth.label(i18n)}
                        </ActionItemButton>
                      ) : (
                        <ActionItemButton
                          data-cy="LogoutMenu"
                          onClick={onLogout}
                        >
                          {adminLinks.auth.label(i18n)}
                        </ActionItemButton>
                      )}
                    </StyledList>

                    <ActionHeading>
                      <Trans data-testid="languages" id="user_menu.language">Language</Trans>
                    </ActionHeading>
                    <StyledList data-testid="locales">
                      {Object.entries(locales).map(([key, val]) => (
                        <ActionItemButton
                          key={val}
                          onClick={selectLocale(key, close)}
                          renderAfter={
                            locales[locale] === val && <CheckSingle tiny />
                          }
                        >
                          {val}
                        </ActionItemButton>
                      ))}
                    </StyledList>
                  </Popover>
                )}
              </Fragment>
            )}
          </Menu>
        </$MenuWrapper>
      )}
    </I18n>
  );
};

UserMenu.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  selectLocale: PropTypes.func.isRequired,
  clientRoutes: PropTypes.shape({
    appLinks: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        label: PropTypes.func,
      })
    ),
    adminLinks: PropTypes.shape({
      support: PropTypes.shape({
        href: PropTypes.string,
        label: PropTypes.func,
      }),
    }),
    userDetails: PropTypes.shape({
      name: PropTypes.string,
      initials: PropTypes.string,
    }),
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserMenu);
